import { DateTime } from 'luxon'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { getSlideQuestions } from '../../../firestore/SlideQuestion'
import { getSlideRubrics } from '../../../firestore/SlideRubric'
import { SimpleObservableValue } from '../../../stores/SimpleObservableValue'
import { Cubit } from '../../core'
import type { AssignNextbookCubit } from '../AssignNextbookCubit'
import { type AssignNextbookSlot, type SlideDeckPackWithId } from '../types'
import { getSlideDeckRoles } from '../../../firestore/SlideDeckRole'
import { getGroupSizeConstraintsFromRoles } from '../../../sources-of-truth/getGroupSizeConstraintsFromRoles'
import { RoomState } from '../../../models/RoomState'
import { isMeetingQuestionScorable } from '../../../sources-of-truth/scoring'

export class AssignNextbookScheduleController extends Cubit {
  cubit: AssignNextbookCubit

  @observable
  slots: AssignNextbookSlot[] | null = null

  isDragging = new SimpleObservableValue<boolean>(false)
  currentlyDragged = new SimpleObservableValue<SlideDeckPackWithId | null>(null)
  mode = new SimpleObservableValue<'sort' | 'sort-no-over' | 'swap'>('swap')

  hasQuizzesBySlideDeckId = new Map<string, boolean>()
  hasRubricsBySlideDeckId = new Map<string, boolean>()
  groupSizeConstraintsBySlideDeckId = observable.map<
    string,
    {
      min: number
      max: number
    }
  >()

  constructor(cubit: AssignNextbookCubit) {
    super()
    this.cubit = cubit
    makeObservable(this)
  }

  initialize() {
    this.initializeSlideDeckStreams()
  }

  initializeSlideDeckStreams() {
    // clear all existing streams
    this.removeStreams('slideDeckQuestions')
    this.removeStreams('slideDeckRubrics')
    this.removeStreams('slideDeckRoles')

    const slideDeckIds = this.cubit.teachingPlanCubit.allSlideDecksPacks.map(
      (pack) => pack.slideDeck.id
    )

    slideDeckIds.forEach((slideDeckId) => {
      this.addSlideDeckQuestionsStream(slideDeckId)
      this.addSlideDeckRubricsStream(slideDeckId)
      this.addSlideDeckRolesStream(slideDeckId)
    })
  }

  addSlideDeckQuestionsStream(slideDeckId: string) {
    this.addStream(
      getSlideQuestions(this.cubit.repository, { slideDeckId }),
      (questions) => {
        this.hasQuizzesBySlideDeckId.set(
          slideDeckId,
          questions.some((q) => isMeetingQuestionScorable(q))
        )
      },
      {
        namespace: 'slideDeckQuestions',
        name: `slideDeckQuestions-${slideDeckId}`,
      }
    )
  }

  addSlideDeckRubricsStream(slideDeckId: string) {
    this.addStream(
      getSlideRubrics(this.cubit.repository, { slideDeckId }),
      (rubrics) => {
        this.hasRubricsBySlideDeckId.set(slideDeckId, rubrics.length > 0)
      },
      {
        namespace: 'slideDeckRubrics',
        name: `slideDeckRubrics-${slideDeckId}`,
      }
    )
  }

  addSlideDeckRolesStream(slideDeckId: string) {
    this.addStream(
      getSlideDeckRoles(this.cubit.repository, { slideDeckId }),
      (roles) => {
        const constraints = getGroupSizeConstraintsFromRoles(roles)
        runInAction(() => {
          this.groupSizeConstraintsBySlideDeckId.set(slideDeckId, constraints)
        })
      },
      {
        namespace: 'slideDeckRoles',
        name: `slideDeckRoles-${slideDeckId}`,
      }
    )
  }

  @computed
  get globalGroupConstraintsToUse() {
    const defaultConstraints = { min: 1, max: RoomState.maxAllowedUsers }

    // if no constraints to check, return defaults
    if (!this.slots?.length || !this.groupSizeConstraintsBySlideDeckId.size)
      return defaultConstraints

    const slideDeckIds = Array.from(
      new Set(
        this.slots
          .map((s) => s.slideDeckPack?.slideDeck.id)
          .filter((a): a is string => Boolean(a))
      )
    )

    const constraints = slideDeckIds.map((id) => {
      return (
        this.groupSizeConstraintsBySlideDeckId.get(id) || defaultConstraints
      )
    })

    // of the constraints, return the lowest low and the highest high
    // we do not want to allow a group size in step 1 that is invalid
    // for ALL assignments being assigned via this dialog
    const min = Math.min(...constraints.map((c) => c.min))
    const max = Math.max(...constraints.map((c) => c.max))
    return { min, max }
  }

  @computed
  get isAnySlotInvalid() {
    if (!this.slots) return false

    return this.slots.some((slot) => {
      const isAfterEndTime =
        this.cubit.sectionEndDateTime &&
        slot.endTime > this.cubit.sectionEndDateTime
      const isBeforeStartTime =
        this.cubit.sectionStartDateTime &&
        slot.startTime < this.cubit.sectionStartDateTime
      return isAfterEndTime || isBeforeStartTime
    })
  }

  @computed
  get slideDecksHaveQuizzes() {
    return this.chosenSlideDecksIds.some((id) => this.slideDeckHasQuizzes(id))
  }

  slideDeckHasQuizzes(slideDeckId: string) {
    return this.hasQuizzesBySlideDeckId.get(slideDeckId) ?? false
  }

  @computed
  get slideDecksHaveRubrics() {
    return this.chosenSlideDecksIds.some((id) => this.slideDeckHasRubrics(id))
  }

  slideDeckHasRubrics(slideDeckId: string) {
    return this.hasRubricsBySlideDeckId.get(slideDeckId) ?? false
  }

  @computed
  get chosenSlideDecksIds() {
    return this.safeSlots
      .map((s) => s.slideDeckPack?.slideDeck.id)
      .filter((a): a is string => Boolean(a))
  }

  @action
  commitSlots() {
    if (!this.slots) return
    const sortedSlots = this.slots.slice().sort((a, b) => {
      return a.endTime.toMillis() - b.endTime.toMillis()
    })
    this.slots = sortedSlots
    const slotsWithSlideDeckPacks = this.slots.filter(
      (slot) => slot.slideDeckPack
    )
    if (slotsWithSlideDeckPacks.length === 0) {
      this.cubit.form.fields.set('assignments', [])
      return
    }
    const assignments = slotsWithSlideDeckPacks.map((slot) => {
      return {
        slideDeckId: slot.slideDeckPack!.slideDeck.id,
        catalogId: slot.slideDeckPack!.catalogId,
        startTime: slot.startTime,
        endTime: slot.endTime,
      }
    })
    this.cubit.form.fields.set('assignments', assignments)
    // changing of assignments resets the grading - grading structure
    // depends on what is in the chosen slide decks
    const gradingOptions = this.cubit.form.fields.get('gradingOptions')
    if (gradingOptions) {
      gradingOptions.scalars = undefined
    }
    this.cubit.storage.save()
  }

  @computed
  get safeSlots(): AssignNextbookSlot[] {
    if (!this.slots) return []
    return this.slots
  }

  @computed
  get firstPossibleEndDate() {
    const sectionStartTime = this.cubit.sectionStartDateTime ?? DateTime.now()
    const timeToComplete = this.cubit.form.fields.get('timeToComplete')
    if (!timeToComplete) return sectionStartTime

    switch (timeToComplete) {
      case '3days':
        return sectionStartTime.plus({ days: 3 })
      case '1week':
        return sectionStartTime.plus({ weeks: 1 })
      case '2weeks':
        return sectionStartTime.plus({ weeks: 2 })
      case '3weeks':
        return sectionStartTime.plus({ weeks: 3 })
      case '1month':
        return sectionStartTime.plus({ months: 1 })
    }
  }

  @computed
  get lastPossibleEndDate() {
    return this.cubit.sectionEndDateTime ?? DateTime.now().plus({ months: 6 })
  }

  @action
  clearSlot(index: number) {
    if (this.slots && this.slots[index]) {
      this.slots[index] = {
        ...this.slots[index],
        slideDeckPack: undefined,
      }
    }
    this.commitSlots()
  }

  @action
  setSlotEndTime(slot: AssignNextbookSlot, endTime: DateTime) {
    if (!this.slots) return
    const index = this.slots?.indexOf(slot)
    if (index === undefined || index === -1) return

    const startTime = this.getStartTime(endTime)
    this.slots[index] = {
      ...this.slots[index],
      endTime: endTime,
      startTime: startTime,
      diff: endTime.diff(startTime, ['days']),
    }
    this.commitSlots()
  }

  @action
  setSlot(index: number, slideDeckPack: SlideDeckPackWithId) {
    const currentIndex = this.slots?.findIndex(
      (slot) => slot.slideDeckPack?.slideDeck.id === slideDeckPack.slideDeck.id
    )
    if (currentIndex !== undefined && currentIndex !== -1) {
      this.clearSlot(currentIndex)
    }
    if (this.slots && this.slots[index]) {
      this.slots[index] = {
        ...this.slots[index],
        slideDeckPack: slideDeckPack,
      }
    }
    this.commitSlots()
  }

  @action
  setAndResortSlots(index: number, slideDeckPack: SlideDeckPackWithId) {
    if (!this.slots) return
    const overItem = this.slots[index].slideDeckPack

    if (!overItem) return

    const slideDeckPacks = this.slots
      .map((slot) => slot.slideDeckPack)
      .filter(Boolean) as SlideDeckPackWithId[]
    const overItemIndex = slideDeckPacks.findIndex(
      (pack) => pack.slideDeck.id === overItem.id
    )

    const currentIndex = slideDeckPacks.findIndex(
      (pack) => pack.slideDeck.id === slideDeckPack.id
    )

    if (currentIndex === -1) {
      // if found, put it in [itemAtIndex] of slideDeckPacks
      slideDeckPacks.splice(overItemIndex, 0, slideDeckPack)
    } else {
      // if found, remove it from currentIndex and put it in [itemAtIndex] of slideDeckPacks
      slideDeckPacks.splice(currentIndex, 1)
      slideDeckPacks.splice(overItemIndex, 0, slideDeckPack)
    }

    for (let slotIndex = 0; slotIndex < this.slots.length; slotIndex++) {
      const slot = this.slots[slotIndex]
      if (!slot) continue
      // skip empty slots
      if (!slot.slideDeckPack) continue
      const pack = slideDeckPacks.shift()
      if (!pack) continue
      slot.slideDeckPack = pack
      this.slots[slotIndex] = slot
    }
    this.commitSlots()
  }

  @action
  putInNextAvailableSlot(slideDeckPack: SlideDeckPackWithId) {
    if (!this.slots) return
    const freeSlotIndex = this.slots.findIndex((slot) => !slot.slideDeckPack)
    if (freeSlotIndex !== -1) {
      this.setSlot(freeSlotIndex, slideDeckPack)
    } else {
      const lastDateTime =
        this.slots[this.slots.length - 1]?.endTime ||
        this.cubit.sectionEndDateTime ||
        DateTime.now()
      const startTime = this.getStartTime(lastDateTime)
      this.slots.push({
        slideDeckPack: slideDeckPack,
        startTime: startTime,
        endTime: lastDateTime,
        diff: lastDateTime.diff(startTime, ['days']),
      })
    }
    this.commitSlots()
  }

  @action loadSlotsFromAssignments() {
    const assignments = this.cubit.form.fields.get('assignments')
    if (!assignments || assignments.length === 0) {
      return false
    }

    try {
      const slots: AssignNextbookSlot[] = []
      for (const assignment of assignments) {
        const slideDeckPack =
          this.cubit.teachingPlanCubit.allSlideDecksPacks.find(
            (pack) =>
              pack.slideDeck.id === assignment.slideDeckId &&
              pack.catalogId === assignment.catalogId
          )
        if (!slideDeckPack) continue
        if (!assignment.startTime.isValid || !assignment.endTime.isValid)
          continue

        const slot: AssignNextbookSlot = {
          slideDeckPack: {
            ...slideDeckPack,
            id: slideDeckPack.slideDeck.id,
          },
          startTime: assignment.startTime,
          endTime: assignment.endTime,
          diff: assignment.endTime.diff(assignment.startTime, ['days']),
        }

        slots.push(slot)
      }
      this.slots = slots
      return true
    } catch (error) {
      console.error('Failed to load slots from assignments:', error)
      this.slots = null
      return false
    }
  }

  @action
  generateSlots() {
    const loaded = this.loadSlotsFromAssignments()
    if (loaded) return

    this.regenerateSlots()
  }

  @action
  regenerateSlots() {
    this.slots = null

    const firstDueDate = this.cubit.form.fields.get('firstDueDate')
    const timeToComplete = this.cubit.form.fields.get('timeToComplete')
    const frequency = this.cubit.form.fields.get('frequency')

    if (!firstDueDate || !timeToComplete || !frequency) return

    const slots: AssignNextbookSlot[] = []
    let currentDueTime = firstDueDate

    const packs = [...this.cubit.teachingPlanCubit.allSlideDecksPacks]
    const slotCount = packs.length

    const sectionStartDateTime =
      this.cubit.sectionStartDateTime ?? DateTime.now()
    const sectionEndDateTime =
      this.cubit.sectionEndDateTime ?? DateTime.now().plus({ months: 6 })

    while (slots.length < slotCount) {
      const pack = packs.shift()
      if (!pack) break // no more packs to assign

      const startTime = this.getStartTime(currentDueTime)
      const endTime = currentDueTime

      const shouldAdd =
        sectionStartDateTime &&
        sectionEndDateTime &&
        startTime > sectionStartDateTime &&
        endTime < sectionEndDateTime

      if (shouldAdd) {
        slots.push({
          slideDeckPack: {
            ...pack,
            id: pack.slideDeck.id,
          },
          startTime: startTime,
          endTime: currentDueTime,
          diff: currentDueTime.diff(startTime, ['days']),
        })
      }

      switch (frequency) {
        case 'weekly':
          currentDueTime = currentDueTime.plus({ weeks: 1 })
          break
        case 'biweekly':
          currentDueTime = currentDueTime.plus({ weeks: 2 })
          break
        case 'monthly':
          currentDueTime = currentDueTime.plus({ months: 1 })
          break
      }
    }

    this.slots = slots
    this.commitSlots()
  }

  getStartTime(endTime: DateTime): DateTime {
    const timeToComplete = this.cubit.form.fields.get('timeToComplete')
    if (!timeToComplete) return endTime

    switch (timeToComplete) {
      case '3days':
        return endTime.minus({ days: 3 })
      case '1week':
        return endTime.minus({ weeks: 1 })
      case '2weeks':
        return endTime.minus({ weeks: 2 })
      case '3weeks':
        return endTime.minus({ weeks: 3 })
      case '1month':
        return endTime.minus({ months: 1 })
    }

    return endTime
  }

  @computed
  get freePacks(): SlideDeckPackWithId[] {
    const slots = this.slots
    if (!slots) return []

    const packs = this.cubit.teachingPlanCubit.allSlideDecksPacks

    if (packs.length === 0) return []
    if (slots.length === 0)
      return packs.map((pack) => ({
        ...pack,
        id: pack.slideDeck.id,
      }))

    const slideDeckIdsInSlots = new Set(
      slots.map((slot) => slot.slideDeckPack?.slideDeck.id)
    )

    // Return packs that are not assigned to any slot
    return packs
      .filter((pack) => !slideDeckIdsInSlots.has(pack.slideDeck.id))
      .map((pack) => ({
        ...pack,
        id: pack.slideDeck.id,
      }))
  }
}
